import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import styled from 'styled-components';
import { useAuth } from 'contexts/AuthContext';
import useTenantCode, { buildPrefsUserId } from 'hooks/useTenantCode';
import {
  PageContainer,
  CommonCss,
  Card,
  SplitLayoutContainer,
  LeftArea,
  RightArea,
} from 'styles/common.styles';
import { theme, SEARCH_LABEL_W } from 'styles/theme';
import { FormArea, CollapsibleSearchPanel, ActionButtonGroup } from 'components/forms';
import { HiGridComponent, useHiGridHook, hiCol } from 'components/grid/hiGrid';
import {
  GridExportProvider,
  GridExportFocusScope,
  useRegisterGridExport,
  useExcelExportAction,
} from 'components/grid/excel';
import {
  ActiveGridProvider,
  ActiveGridScope,
  useActiveGridCrudActions,
} from 'contexts/ActiveGridContext';
import { showAlert } from 'utils/toastBridge';
import useScrollbarHoverProps from 'hooks/useScrollbarHoverProps';
import { hoverExpandScrollbarCss } from 'styles/scrollbar.styles';
import { BUDGET_TYPE_ROW_OPTIONS } from 'budget/budgetStructureStore';
import {
  buildBudgetLevelColumnFields,
  loadProjectStructurePayload,
} from 'budget/budgetProjectStructureStore';
import {
  YN_OPTIONS,
  buildSubjectTree,
  PROJECT_ATTR_DEFAULTS,
  defaultProjectRow,
  loadProjects,
  saveProjects,
  getConfirmedProjectStructure,
  STRUCTURE_BUDGET_TYPE,
  PROJECT_PURPOSE,
  isGnkTenant,
  clearProjectStorageForTenant,
  GNK_PROJECT_RESET_FLAG,
} from 'budget/budgetProjectStore';
import {
  createProjectUid,
  ensureProjectsIdentity,
  buildProjectPathCode,
  ensureSampleProjectHistory,
  upsertHistoryForProjects,
  loadProjectHistory,
} from 'budget/budgetProjectHistoryStore';
import { ensureGnkProjectBootstrap, GNK_PROJECT_SOURCE } from 'budget/seed/gnkProjects';

const GRID_ID = 'budget-project-register';
const HISTORY_GRID_ID = 'budget-project-history';

/** 정렬순서 기본값: 기존 최댓값 다음의 10 단위 (10, 20, 30 …) */
function nextSortOrderBy10(rows) {
  let max = 0;
  for (const r of rows || []) {
    const n = Number(r?.sortOrder);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return Math.ceil((max + 1) / 10) * 10;
}

const TreePanel = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  padding: 0;
  overflow: hidden;
`;

const PanelHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
  padding: 10px 12px;
  border-bottom: 1px solid ${theme.colors.border};
  font-weight: 700;
  font-size: ${theme.typography.fontSize.sm};
`;

const TREE_ROOT_ID = '__ROOT__';

const TreeScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow-x: auto;
  overflow-y: auto;
  padding: 4px 2px 12px;
  ${hoverExpandScrollbarCss}
`;

const TreeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  width: max-content;
  min-width: 100%;
  box-sizing: border-box;
  padding: 3px 6px 3px ${(p) => 6 + p.$depth * 14}px;
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : 'transparent')};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.text.primary)};
  user-select: none;
  &:hover {
    background: ${theme.colors.primaryLight};
  }
`;

const ToggleBtn = styled.button`
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  padding: 0;
  margin: 0;
  border: 1px solid #9CA3AF;
  border-radius: 2px;
  background: #fff;
  color: #374151;
  font-size: 11px;
  line-height: 14px;
  font-weight: 700;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

const Caret = styled.span`
  flex: 0 0 12px;
  width: 12px;
  color: #16A34A;
  font-size: 10px;
  line-height: 1;
  text-align: center;
`;

const LeafDot = styled.span`
  flex: 0 0 16px;
  width: 16px;
  text-align: center;
  color: #9CA3AF;
  font-size: 10px;
  line-height: 1;
`;

const TreeLabel = styled.span`
  flex: 0 0 auto;
  white-space: nowrap;
`;

const rowKey = (r) => `${r?.budgetType || ''}::${r?.projectCode || ''}`;

const treeIdOf = (n) => n?._treeId || `${n?.budgetType || ''}::${n?.projectCode || ''}`;

function stripTreeFields(n) {
  if (!n) return n;
  const { subRows, _treeId, ...rest } = n;
  return rest;
}

function flattenSubtree(node) {
  const out = [];
  const walk = (n) => {
    if (!n) return;
    out.push(stripTreeFields(n));
    (n.subRows || []).forEach(walk);
  };
  walk(node);
  return out;
}

function findTreeNode(nodes, treeId) {
  for (const n of nodes || []) {
    if (treeIdOf(n) === treeId) return n;
    const found = findTreeNode(n.subRows, treeId);
    if (found) return found;
  }
  return null;
}

function collectExpandableIds(nodes, depth = 0, maxDepth = 1) {
  const ids = [];
  (nodes || []).forEach((n) => {
    if (n.subRows?.length) {
      if (depth <= maxDepth) ids.push(treeIdOf(n));
      ids.push(...collectExpandableIds(n.subRows, depth + 1, maxDepth));
    }
  });
  return ids;
}

/** 전체 펼치기용 — 하위 노드 id 전부 */
function collectAllExpandableIds(nodes) {
  return collectExpandableIds(nodes, 0, Number.MAX_SAFE_INTEGER);
}

const TreeHeadActions = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
`;

const TreeToggleBtn = styled.button`
  padding: 2px 8px;
  min-height: 22px;
  border: 1px solid ${(p) => (p.$active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${theme.borderRadius.sm};
  background: ${(p) => (p.$active ? theme.colors.primaryLight : theme.colors.white)};
  color: ${(p) => (p.$active ? theme.colors.primary : theme.colors.text.secondary)};
  font-size: ${theme.typography.fontSize.xxs};
  font-weight: ${(p) => (p.$active ? 600 : 400)};
  cursor: pointer;
  line-height: 1.2;
  &:hover {
    border-color: ${theme.colors.primary};
    color: ${theme.colors.primary};
  }
`;

function TreeNodes({ nodes, depth, expandedIds, selectedTreeId, onToggle, onSelect }) {
  return (nodes || []).map((n) => {
    const id = treeIdOf(n);
    const hasChildren = !!n.subRows?.length;
    const expanded = expandedIds.has(id);
    const label = `[${n.levelName}] ${n.projectCode} ${n.projectName}`;
    return (
      <React.Fragment key={id}>
        <TreeRow
          $depth={depth}
          $active={selectedTreeId === id}
          onClick={() => onSelect(n)}
          title={`${n.levelName} ${n.projectCode}`}
        >
          {hasChildren ? (
            <ToggleBtn
              type="button"
              title={expanded ? '접기' : '펼치기'}
              onClick={(e) => {
                e.stopPropagation();
                onToggle(id);
              }}
            >
              {expanded ? '−' : '+'}
            </ToggleBtn>
          ) : (
            <LeafDot>•</LeafDot>
          )}
          {hasChildren ? <Caret>{expanded ? '▼' : '▶'}</Caret> : <Caret />}
          <TreeLabel>{label}</TreeLabel>
        </TreeRow>
        {hasChildren && expanded && (
          <TreeNodes
            nodes={n.subRows}
            depth={depth + 1}
            expandedIds={expandedIds}
            selectedTreeId={selectedTreeId}
            onToggle={onToggle}
            onSelect={onSelect}
          />
        )}
      </React.Fragment>
    );
  });
}

function ProjectTree({ nodes, expandedIds, selectedTreeId, onToggle, onSelectRoot, onSelectNode }) {
  const rootExpanded = expandedIds.has(TREE_ROOT_ID);
  return (
    <>
      <TreeRow
        $depth={0}
        $active={selectedTreeId === TREE_ROOT_ID}
        onClick={onSelectRoot}
        title="전체 사업"
      >
        <ToggleBtn
          type="button"
          title={rootExpanded ? '접기' : '펼치기'}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(TREE_ROOT_ID);
          }}
        >
          {rootExpanded ? '−' : '+'}
        </ToggleBtn>
        <Caret>{rootExpanded ? '▼' : '▶'}</Caret>
        <TreeLabel>사업구조</TreeLabel>
      </TreeRow>
      {rootExpanded && (
        <TreeNodes
          nodes={nodes}
          depth={1}
          expandedIds={expandedIds}
          selectedTreeId={selectedTreeId}
          onToggle={onToggle}
          onSelect={onSelectNode}
        />
      )}
      {!nodes?.length && rootExpanded && (
        <div style={{ padding: '8px 12px 8px 36px', color: '#9CA3AF', fontSize: '0.85rem' }}>
          등록된 사업이 없습니다.
        </div>
      )}
    </>
  );
}

const RightStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  height: 100%;
  min-height: 0;
`;

const GridPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  flex: 1.4 1 0;
  min-height: 220px;
  padding: 0;
  overflow: hidden;
`;

const AttrPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  min-height: 100px;
  max-height: 180px;
  padding: 0;
  overflow: auto;
`;

const HistoryPanel = styled(Card)`
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 180px;
  padding: 0;
  overflow: hidden;
`;

/** height:100% 그리드가 높이 0으로 붕괴되지 않도록 flex 체인 유지 */
const GridBody = styled.div`
  flex: 1 1 0;
  min-height: 0;
  min-width: 0;
  padding: 8px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const GateBanner = styled.div`
  margin: 8px 0 12px;
  padding: 12px 14px;
  border-radius: ${theme.borderRadius.md};
  background: #FEF3C7;
  color: #92400E;
  font-size: ${theme.typography.fontSize.sm};
  line-height: 1.5;
`;

const StructureBanner = styled.div`
  margin: 0 0 10px;
  padding: 8px 12px;
  border-radius: ${theme.borderRadius.sm};
  background: ${theme.colors.primaryLight};
  color: ${theme.colors.primary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: 600;
`;

/**
 * 사업등록 — 확정된 사업구조 레벨 기준 단일 테이블에서 사업 관리
 */
const BudgetProjectRegisterInner = () => {
  const gridRef = useRef(null);
  const historyGridRef = useRef(null);
  const { user } = useAuth();
  const tenantCode = useTenantCode();
  const prefsUserId = useMemo(() => buildPrefsUserId(tenantCode, user), [tenantCode, user]);
  const { handleActiveAdd, handleActiveDelete, activeLabel, activeGridId } = useActiveGridCrudActions({
    guardAdd: (id) =>
      id === HISTORY_GRID_ID ? '이력은 조회 전용입니다. 기본정보 그리드에서 추가하세요.' : null,
    guardDelete: (id) => (id === HISTORY_GRID_ID ? '이력은 삭제할 수 없습니다.' : null),
  });
  const year = String(new Date().getFullYear());
  const treeScrollbarHover = useScrollbarHoverProps();

  const searchDefaults = {
    tenantCode,
    fiscalYear: year,
  };

  const methods = useForm({ mode: 'onSubmit', defaultValues: { ...searchDefaults } });
  const { getValues, setValue } = methods;

  useEffect(() => {
    setValue('tenantCode', tenantCode);
  }, [tenantCode, setValue]);

  // GNK: 기존 사업·이력 1회 정리 후 엑셀 시드 적재 가능하도록
  useEffect(() => {
    if (!isGnkTenant(tenantCode)) return;
    if (localStorage.getItem(GNK_PROJECT_RESET_FLAG) === 'v2') return;
    clearProjectStorageForTenant(tenantCode);
    localStorage.setItem(GNK_PROJECT_RESET_FLAG, 'v2');
  }, [tenantCode]);

  const detailDefaults = { ...PROJECT_ATTR_DEFAULTS };
  const detailMethods = useForm({ mode: 'onChange', defaultValues: detailDefaults });
  const { reset: resetDetail, getValues: getDetailValues } = detailMethods;

  const [structure, setStructure] = useState(null);
  const [allRows, setAllRows] = useState([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTreeId, setSelectedTreeId] = useState(TREE_ROOT_ID);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [expandedIds, setExpandedIds] = useState(() => new Set([TREE_ROOT_ID]));
  const filterKeysRef = useRef(new Set());
  const allRowsRef = useRef([]);
  allRowsRef.current = allRows;

  const dimsFromForm = useCallback(() => {
    const v = getValues();
    return {
      tenantCode: v.tenantCode,
      fiscalYear: v.fiscalYear,
      purpose: PROJECT_PURPOSE,
    };
  }, [getValues]);

  const levelOptions = useMemo(() => {
    const used = structure?.usedLevels || [];
    return used.map((l) => ({ value: l.levelName, label: l.levelName }));
  }, [structure]);

  const columns = useMemo(
    () => [
      hiCol.edComboColumn({
        header: '레벨',
        field: 'levelName',
        size: 100,
        options: levelOptions.length ? levelOptions : [{ value: '', label: '-' }],
        required: true,
      }),
      hiCol.edComboColumn({
        header: '예산구분',
        field: 'budgetType',
        size: 110,
        options: BUDGET_TYPE_ROW_OPTIONS,
        required: true,
      }),
      hiCol.edTextColumn({ header: '상위코드', field: 'parentCode', size: 100 }),
      hiCol.edTextColumn({ header: '사업코드', field: 'projectCode', size: 110, required: true }),
      hiCol.edTextColumn({ header: '사업명', field: 'projectName', size: 140, required: true }),
      hiCol.roTextColumn({ header: '내부코드', field: 'projectUid', size: 160, minSize: 120, visible: false }),
      hiCol.roTextColumn({ header: '경로코드', field: 'pathCode', size: 220, minSize: 200 }),
      hiCol.edComboColumn({ header: '사용', field: 'useYn', size: 100, options: YN_OPTIONS }),
      hiCol.edTextColumn({ header: '시작연도', field: 'startYear', size: 100, align: 'center' }),
      hiCol.edTextColumn({ header: '종료연도', field: 'endYear', size: 100, align: 'center' }),
      hiCol.edNumberColumn({ header: '정렬순서', field: 'sortOrder', size: 110, align: 'center' }),
    ],
    [levelOptions]
  );

  const grid = useHiGridHook({
    gridRef,
    getSearchValues: getValues,
    autoSearch: false,
    enableContextMenu: true,
    confirmCheck: true,
    defaultRow: defaultProjectRow(),
  });

  const historyColumns = useMemo(
    () => [
      hiCol.roTextColumn({ header: '회계연도', field: 'fiscalYear', size: 100, minSize: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '사업순번', field: 'seqNo', size: 100, minSize: 100, align: 'center' }),
      hiCol.roTextColumn({ header: '사업코드', field: 'projectCode', size: 100 }),
      hiCol.roTextColumn({ header: '사업명', field: 'projectName', size: 140 }),
      hiCol.roTextColumn({ header: '레벨', field: 'levelName', size: 90, minSize: 90 }),
      hiCol.roTextColumn({ header: '내부코드', field: 'projectUid', size: 160, minSize: 120, visible: false }),
      hiCol.roTextColumn({ header: '경로코드', field: 'pathCode', size: 160, minSize: 120 }),
      hiCol.roTextColumn({ header: '입력', field: 'inputYn', size: 90, minSize: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '사용', field: 'useYn', size: 90, minSize: 90, align: 'center' }),
      hiCol.roTextColumn({ header: '비고', field: 'remark', size: 200 }),
    ],
    []
  );

  const refreshHistoryFor = useCallback(
    (row) => {
      if (!row?.projectUid) {
        historyGridRef.current?.setData?.([]);
        return;
      }
      const tenant = getValues('tenantCode') || tenantCode;
      const fy = getValues('fiscalYear') || year;
      const list = isGnkTenant(tenant)
        ? loadProjectHistory(tenant, row.projectUid)
        : ensureSampleProjectHistory(tenant, row, fy, 3);
      historyGridRef.current?.setData?.(list);
    },
    [getValues, tenantCode, year]
  );

  const applyDetailFromRow = useCallback(
    (row, rowIndex = -1) => {
      if (!row) {
        setSelectedCode('');
        setSelectedProject(null);
        setSelectedIndex(-1);
        resetDetail(detailDefaults);
        historyGridRef.current?.setData?.([]);
        return;
      }
      setSelectedCode(row.projectCode || '');
      setSelectedProject(row);
      setSelectedIndex(rowIndex);
      resetDetail({
        fieldSectorNm: row.fieldSectorNm || '',
        budgetDeptNm: row.budgetDeptNm || '',
        inputYn: row.inputYn || 'Y',
      });
      refreshHistoryFor(row);
    },
    [resetDetail, refreshHistoryFor]
  );

  const treeNodes = useMemo(
    () => buildSubjectTree(allRows, { idKey: 'projectCode', parentKey: 'parentCode' }),
    [allRows]
  );

  const showRowsForFilter = useCallback(
    (filterId, rows) => {
      const list = rows || allRowsRef.current;
      let view = list;
      if (filterId && filterId !== TREE_ROOT_ID) {
        const tree = buildSubjectTree(list, { idKey: 'projectCode', parentKey: 'parentCode' });
        const node = findTreeNode(tree, filterId);
        view = node ? flattenSubtree(node) : [];
      }
      filterKeysRef.current = new Set(view.map(rowKey).filter((k) => k !== '::'));
      grid.setData(view);
      setSelectedTreeId(filterId || TREE_ROOT_ID);
      if (view[0]) applyDetailFromRow(view[0], 0);
      else applyDetailFromRow(null);
    },
    [grid, applyDetailFromRow]
  );

  const mergeGridIntoAllRows = useCallback(() => {
    const gridRows = gridRef.current?.getData?.() || [];
    const prev = allRowsRef.current;
    const filterKeys = filterKeysRef.current;
    const gridMap = new Map(gridRows.filter((r) => r.projectCode).map((r) => [rowKey(r), r]));

    if (!filterKeys.size || selectedTreeId === TREE_ROOT_ID) {
      return gridRows.filter((r) => r.projectCode);
    }

    const next = [];
    const seen = new Set();
    prev.forEach((r) => {
      const k = rowKey(r);
      if (filterKeys.has(k)) {
        if (gridMap.has(k)) {
          next.push({ ...r, ...gridMap.get(k) });
          seen.add(k);
        }
      } else {
        next.push(r);
        seen.add(k);
      }
    });
    gridMap.forEach((r, k) => {
      if (!seen.has(k)) next.push(r);
    });
    return next;
  }, [selectedTreeId]);

  const syncFromGridEdits = useCallback(() => {
    const merged = mergeGridIntoAllRows();
    setAllRows(merged);
  }, [mergeGridIntoAllRows]);

  useEffect(() => {
    grid.onRowClicked = ({ data, rowIndex }) => applyDetailFromRow(data, rowIndex);
    grid.onSelectRow = (rowData, rowIndex) => applyDetailFromRow(rowData, rowIndex);
    grid.onAfterSaveCell = () => syncFromGridEdits();
    grid.onAfterDataLoaded = () => {};
  }, [grid, applyDetailFromRow, syncFromGridEdits]);

  const loadAll = useCallback(async () => {
    const dims = dimsFromForm();
    let boot = null;
    if (isGnkTenant(dims.tenantCode)) {
      boot = await ensureGnkProjectBootstrap(dims, { userId: user?.id || user?.name });
    }
    // getConfirmedProjectStructure는 동기 캐시 조회 — API 모드에서는 먼저 적재해야 한다.
    await loadProjectStructurePayload(dims);
    const confirmed = boot?.structure || getConfirmedProjectStructure(dims);
    setStructure(confirmed);
    if (!confirmed) {
      grid.setData([]);
      setAllRows([]);
      filterKeysRef.current = new Set();
      setSelectedTreeId(TREE_ROOT_ID);
      applyDetailFromRow(null);
      return { boot, confirmed: null, rows: [] };
    }

    // 부트스트랩이 이미 loadProjects 했으면 재조회하지 않음. 이력 upsert는 저장·연도복사만.
    let rows = boot?.projects?.length ? boot.projects : await loadProjects(dims);
    rows = ensureProjectsIdentity(rows);
    setAllRows(rows);
    const tree = buildSubjectTree(rows, { idKey: 'projectCode', parentKey: 'parentCode' });
    const initExpand = new Set([TREE_ROOT_ID, ...collectExpandableIds(tree, 0, 0)]);
    setExpandedIds(initExpand);
    showRowsForFilter(TREE_ROOT_ID, rows);
    return { boot, confirmed, rows };
  }, [dimsFromForm, grid, applyDetailFromRow, showRowsForFilter, user]);

  useEffect(() => {
    loadAll()
      .then((result) => {
        if (result?.boot?.seeded) {
          showAlert(
            `GNK 사업을 등록했습니다. (${result.boot.seedCount}건)\n${GNK_PROJECT_SOURCE}`,
            'success'
          );
        }
      })
      .catch((e) => {
        showAlert(e?.message || '사업 조회에 실패했습니다.', 'error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = useCallback(async () => {
    let result;
    try {
      result = (await loadAll()) || {};
    } catch (e) {
      showAlert(e?.message || '조회에 실패했습니다.', 'error');
      return;
    }
    const { boot, confirmed, rows } = result;
    if (!confirmed) {
      showAlert('해당 조건으로 확정된 사업구조가 없습니다.\n먼저 사업구조화관리에서 구조를 확정하세요.', 'warning');
      return;
    }
    if (boot?.seeded) {
      showAlert(
        `GNK 사업 시드를 적재했습니다. (${rows?.length || boot.seedCount}건)\n구조: ${confirmed.usedLevels
          .map((l) => l.levelName)
          .join(' > ')}`,
        'success'
      );
      return;
    }
    showAlert(
      `구조를 반영했습니다: ${confirmed.usedLevels.map((l) => l.levelName).join(' > ')} (${rows?.length || 0}건)`,
      'success'
    );
  }, [loadAll]);

  const pushDetailToGrid = useCallback(() => {
    if (selectedIndex < 0) return;
    const d = getDetailValues();
    grid.setCellValues?.(selectedIndex, {
      fieldSectorNm: d.fieldSectorNm || '',
      budgetDeptNm: d.budgetDeptNm || '',
      inputYn: d.inputYn || 'Y',
    });
  }, [selectedIndex, getDetailValues, grid]);

  const handleSave = useCallback(async () => {
    if (!structure) {
      showAlert('구조가 확정되지 않아 저장할 수 없습니다.', 'warning');
      return;
    }
    pushDetailToGrid();
    const mergedRaw = mergeGridIntoAllRows();
    const merged = ensureProjectsIdentity(
      mergedRaw.map((r) => ({
        ...r,
        pathCode: buildProjectPathCode(r, mergedRaw),
        projectUid: r.projectUid || createProjectUid(),
      }))
    );
    const invalid = merged.find((r) => !r.projectCode || !r.projectName || !r.levelName || !r.budgetType);
    if (invalid) {
      showAlert('레벨·예산구분·사업코드·사업명은 필수입니다.', 'warning');
      return;
    }
    const dims = dimsFromForm();
    try {
      await saveProjects(dims, merged);
    } catch (e) {
      showAlert(e?.message || '저장에 실패했습니다.', 'error');
      return;
    }
    upsertHistoryForProjects(dims.tenantCode, merged, dims.fiscalYear);
    setAllRows(merged);
    showRowsForFilter(selectedTreeId, merged);
    if (selectedProject?.projectUid) {
      const updated = merged.find((r) => r.projectUid === selectedProject.projectUid);
      refreshHistoryFor(updated || selectedProject);
    }
    showAlert('사업이 저장되었습니다.', 'success');
  }, [
    structure,
    pushDetailToGrid,
    dimsFromForm,
    mergeGridIntoAllRows,
    showRowsForFilter,
    selectedTreeId,
    selectedProject,
    refreshHistoryFor,
  ]);

  const handleAdd = useCallback(() => {
    if (!structure) {
      showAlert('구조 확정 후 사업을 등록할 수 있습니다.', 'warning');
      return;
    }
    const firstLevel = structure.usedLevels[0]?.levelName || '';
    const defaultBt = STRUCTURE_BUDGET_TYPE;
    const treeNode =
      selectedTreeId && selectedTreeId !== TREE_ROOT_ID
        ? findTreeNode(treeNodes, selectedTreeId)
        : null;
    const parentCode = treeNode?.projectCode || selectedCode || '';
    const budgetType = treeNode?.budgetType || defaultBt;
    const levelName = treeNode
      ? structure.usedLevels.find((l) => l.levelName === treeNode.levelName)
        ? structure.usedLevels[
            Math.min(
              structure.usedLevels.findIndex((l) => l.levelName === treeNode.levelName) + 1,
              structure.usedLevels.length - 1
            )
          ]?.levelName || firstLevel
        : firstLevel
      : firstLevel;
    const gridData = gridRef.current?.getData?.() || [];
    const sortPool = [...(allRowsRef.current || []), ...gridData];
    grid.handleAdd(
      defaultProjectRow({
        levelName,
        budgetType,
        parentCode,
        projectCode: '',
        projectName: '',
        projectUid: createProjectUid(),
        pathCode: '',
        useYn: 'Y',
        startYear: '',
        endYear: '',
        sortOrder: nextSortOrderBy10(sortPool),
        ...PROJECT_ATTR_DEFAULTS,
      })
    );
  }, [structure, grid, selectedCode, selectedTreeId, treeNodes]);

  const handleDelete = useCallback(() => {
    grid.handleDelete();
    setTimeout(() => {
      const merged = mergeGridIntoAllRows();
      setAllRows(merged);
      showRowsForFilter(selectedTreeId, merged);
    }, 0);
  }, [grid, mergeGridIntoAllRows, showRowsForFilter, selectedTreeId]);

  const handleToggleExpand = useCallback((id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const allExpandableIds = useMemo(
    () => [TREE_ROOT_ID, ...collectAllExpandableIds(treeNodes)],
    [treeNodes]
  );

  const isTreeAllExpanded = useMemo(() => {
    if (!allExpandableIds.length) return false;
    return allExpandableIds.every((id) => expandedIds.has(id));
  }, [allExpandableIds, expandedIds]);

  const handleTreeExpandCollapseAll = useCallback(() => {
    if (isTreeAllExpanded) {
      setExpandedIds(new Set());
      return;
    }
    setExpandedIds(new Set(allExpandableIds));
  }, [isTreeAllExpanded, allExpandableIds]);

  const handleSelectRoot = useCallback(() => {
    const merged = mergeGridIntoAllRows();
    setAllRows(merged);
    showRowsForFilter(TREE_ROOT_ID, merged);
  }, [mergeGridIntoAllRows, showRowsForFilter]);

  const handleSelectTreeNode = useCallback(
    (node) => {
      const merged = mergeGridIntoAllRows();
      setAllRows(merged);
      showRowsForFilter(treeIdOf(node), merged);
      setSelectedCode(node.projectCode || '');
    },
    [mergeGridIntoAllRows, showRowsForFilter]
  );

  const getExportData = useCallback(
    () => gridRef.current?.getFilteredData?.() ?? gridRef.current?.getData?.() ?? [],
    []
  );
  const getExportColumns = useCallback(() => gridRef.current?.getDataColumns?.() ?? [], []);

  useRegisterGridExport({
    id: GRID_ID,
    label: '사업등록',
    getData: getExportData,
    getDataColumns: getExportColumns,
    joinKeys: ['budgetType', 'projectCode'],
    order: 0,
  });

  const { excelButton, dialog: excelDialog } = useExcelExportAction({ fileName: '사업등록' });

  const searchFields = [
    [
      {
        name: 'fiscalYear',
        label: '회계연도',
        type: 'fiscalYear',
        width: CommonCss.w90,
        labelWidth: SEARCH_LABEL_W,
        min: 2000,
        max: 2099,
      },
    ],
  ];

  const handleAttrCodeSearch = useCallback(({ name }) => {
    const labels = {
      fieldSectorNm: '분야·부문',
      budgetDeptNm: '전담예산부서',
    };
    showAlert(
      `[${labels[name] || name}] 코드 조회 팝업은 공통코드/매핑 테이블 연동 후 연결됩니다.`,
      'info'
    );
  }, []);

  const ATTR_LABEL_W = '72px';
  const detailFields = [
    [
      {
        name: 'fieldSectorNm',
        label: '분야·부문',
        type: 'text',
        width: CommonCss.w160,
        labelWidth: ATTR_LABEL_W,
        showSearch: true,
        searchTitle: '분야·부문 코드 조회',
        onSearch: handleAttrCodeSearch,
      },
      {
        name: 'budgetDeptNm',
        label: '전담예산부서',
        type: 'text',
        width: CommonCss.w160,
        labelWidth: ATTR_LABEL_W,
        showSearch: true,
        searchTitle: '전담예산부서 코드 조회',
        onSearch: handleAttrCodeSearch,
      },
      {
        name: 'inputYn',
        label: '입력여부',
        type: 'select',
        width: CommonCss.w80,
        labelWidth: ATTR_LABEL_W,
        options: YN_OPTIONS,
      },
    ],
  ];

  /** 공통 기능 버튼: 조회 / 추가 / 삭제 / 저장 / 엑셀 */
  const actionButtons = [
    { id: 'search', label: '조회', icon: '🔍', onClick: handleSearch, hotkey: 'F7' },
    {
      id: 'add',
      label: '추가',
      icon: '➕',
      onClick: handleActiveAdd,
      hotkey: 'F8',
      title: activeLabel ? `추가 → ${activeLabel} (F8)` : '추가 (F8) · 그리드 선택 Alt+F2',
    },
    {
      id: 'delete',
      label: '삭제',
      icon: '❌',
      onClick: handleActiveDelete,
      hotkey: 'F9',
      variant: 'danger',
      title: activeLabel ? `삭제 → ${activeLabel} (F9)` : '삭제 (F9) · 그리드 선택 Alt+F2',
    },
    { id: 'save', label: '저장', icon: '✅', onClick: handleSave, hotkey: 'F10', variant: 'success' },
    excelButton,
  ];

  const pathText = structure
    ? structure.usedLevels.map((l) => l.levelName).join(' > ')
    : '';
  const columnHint = structure
    ? buildBudgetLevelColumnFields(structure.usedLevels)
        .map((c) => c.header)
        .join(' · ')
    : '';

  return (
    <PageContainer>
      <FormProvider {...methods}>
        <ActionButtonGroup buttons={actionButtons} loading={grid.loading} />
        <CollapsibleSearchPanel
          storageKey="budget-project-register"
          prefsUserId={prefsUserId}
          summary="회계연도 조회"
        >
          <FormArea fields={searchFields} compact onFieldEnter={handleSearch} defaultValues={searchDefaults} />
        </CollapsibleSearchPanel>
      </FormProvider>

      {!structure && (
        <GateBanner>
          사업구조가 확정되지 않았습니다.
          <strong> 사업구조화관리</strong>에서 구조를 확정한 뒤 조회하세요.
        </GateBanner>
      )}

      {structure && (
        <StructureBanner>
          확정 구조: {pathText}
          <span style={{ fontWeight: 400, marginLeft: 12 }}>
            → 편성·배정·품의·원인행위·지출결의 컬럼: {columnHint}
            {structure.applyLevelName ? ` · 적용레벨: ${structure.applyLevelName}` : ''}
          </span>
        </StructureBanner>
      )}

      <SplitLayoutContainer style={{ flex: 1, minHeight: 0 }}>
        <LeftArea style={{ flex: '0 0 280px', width: 280, minWidth: 240, maxWidth: 320 }}>
          <TreePanel>
            <PanelHead>
              <span>사업트리</span>
              <TreeHeadActions>
                <TreeToggleBtn
                  type="button"
                  $active={isTreeAllExpanded}
                  title={isTreeAllExpanded ? '전체 접기' : '전체 펼치기'}
                  onClick={handleTreeExpandCollapseAll}
                >
                  {isTreeAllExpanded ? '접기' : '펼치기'}
                </TreeToggleBtn>
              </TreeHeadActions>
            </PanelHead>
            <TreeScroll {...treeScrollbarHover}>
              <ProjectTree
                nodes={treeNodes}
                expandedIds={expandedIds}
                selectedTreeId={selectedTreeId}
                onToggle={handleToggleExpand}
                onSelectRoot={handleSelectRoot}
                onSelectNode={handleSelectTreeNode}
              />
            </TreeScroll>
          </TreePanel>
        </LeftArea>

        <RightArea>
          <RightStack>
            <GridPanel>
              <PanelHead>
                <span>사업리스트</span>
                <span style={{ fontWeight: 500, fontSize: '0.75rem', color: '#6B7280' }}>단일 사업 테이블 기반</span>
              </PanelHead>
              <GridBody>
                <ActiveGridScope
                  gridId={GRID_ID}
                  label="사업리스트"
                  order={0}
                  onAdd={handleAdd}
                  onDelete={handleDelete}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <GridExportFocusScope gridId={GRID_ID} style={{ flex: 1, minHeight: 0, height: '100%' }}>
                    <HiGridComponent
                      ref={gridRef}
                      columns={columns}
                      height="100%"
                      showRowNumber
                      rowNumberSize={56}
                      showModColumn={false}
                      headerActive={activeGridId === GRID_ID}
                      {...grid.contextMenuProps}
                      enableHeaderMenu
                      enableSelectionStats
                      enableFreeze
                      enableColumnFilters
                      enableQuickExport
                      exportFileName="사업등록"
                      gridId={GRID_ID}
                      prefsUserId={prefsUserId}
                      colOptions={grid.colOptions}
                    />
                  </GridExportFocusScope>
                </ActiveGridScope>
              </GridBody>
            </GridPanel>

            <AttrPanel>
              <PanelHead>
                <span>속성정보</span>
                <span style={{ fontWeight: 500, fontSize: '0.75rem', color: '#6B7280' }}>
                  선택행 기준{selectedCode ? ` · ${selectedCode}` : ''}
                </span>
              </PanelHead>
              <div style={{ padding: '8px 12px 12px' }}>
                <FormProvider {...detailMethods}>
                  <FormArea
                    fields={detailFields}
                    dense
                    defaultValues={detailDefaults}
                  />
                </FormProvider>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: '#6B7280' }}>
                  속성 변경은 저장(F10) 시 선택 행에 반영됩니다.
                </p>
              </div>
            </AttrPanel>

            <HistoryPanel>
              <PanelHead>
                <span>연도별 사업이력</span>
                <span style={{ fontWeight: 500, fontSize: '0.75rem', color: '#6B7280' }}>
                  {selectedProject?.projectCode
                    ? `선택: ${selectedProject.projectCode} ${selectedProject.projectName || ''}`
                    : '상단 사업을 선택하면 연도별 이력이 조회됩니다'}
                </span>
              </PanelHead>
              <GridBody>
                <ActiveGridScope
                  gridId={HISTORY_GRID_ID}
                  label="사업이력"
                  order={1}
                  canAdd={false}
                  canDelete={false}
                  style={{ flex: 1, minHeight: 0, height: '100%' }}
                >
                  <HiGridComponent
                    ref={historyGridRef}
                    columns={historyColumns}
                    height="100%"
                    showRowNumber
                    rowNumberSize={56}
                    showModColumn={false}
                    headerActive={activeGridId === HISTORY_GRID_ID}
                    enableHeaderMenu
                    enableColumnFilters
                    enableFreeze
                    enableQuickExport
                    exportFileName="사업이력"
                    gridId={HISTORY_GRID_ID}
                    prefsUserId={prefsUserId}
                  />
                </ActiveGridScope>
              </GridBody>
            </HistoryPanel>
          </RightStack>
        </RightArea>
      </SplitLayoutContainer>
      {excelDialog}
    </PageContainer>
  );
};

const BudgetProjectRegister = () => (
  <GridExportProvider>
    <ActiveGridProvider>
      <BudgetProjectRegisterInner />
    </ActiveGridProvider>
  </GridExportProvider>
);

export default BudgetProjectRegister;
